import api from "./api";

/**
 * Get user by UUID
 * @param {string} userUuid - User UUID
 * @returns {Promise<Object>} User data
 */
export const getUserByUuid = async (userUuid) => {
  if (!userUuid) {
    throw new Error("userUuid is required");
  }

  try {
    const response = await api.get(`/v1/tenant/user/${userUuid}`);
    const responseData = response.data;

    if (!responseData) {
      throw new Error('Invalid response from server');
    }

    const statusCode = responseData.status_code || responseData.statusCode || response?.status || 200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    const responseBody = responseData.body || null;

    if (statusCode >= 200 && statusCode < 300) {
      return {
        body: responseBody,
        status_code: statusCode,
        meta: responseData.meta || null
      };
    }

    const errorMessage = msgCode.length > 0 
      ? (msgCode[0].description || msgCode[0].code || 'An error occurred while fetching user.')
      : 'An error occurred while fetching user.';

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };
    throw error;

  } catch (error) {
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      const errorMessage = responseData.message || error.message;

      const apiError = new Error(errorMessage);
      apiError.response = {
        data: responseData,
        status: error.response.status,
      };
      throw apiError;
    }

    throw error;
  }
};

/**
 * Get user list (uses /v1/tenant/user/list endpoint)
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.order_by - Field to order by
 * @param {string} params.order_direction - Order direction (ASC/DESC)
 * @param {string|Object} params.search - Search keyword or search object
 * @returns {Promise<Object>} User list data with body and meta
 */
export const getUserList = async (params = {}) => {
  const page = params.page || 1;
  const pageSize = params.page_size || 10;

  // API expects camelCase: createdAt, email, fullName, status, updatedAt
  const orderByMap = {
    createdAt: "createdAt",
    fullName: "fullName",
    updatedAt: "updatedAt",
    created_at: "createdAt",
    full_name: "fullName",
    updated_at: "updatedAt",
    name: "fullName",
    email: "email",
    status: "status",
  };

  const orderBy = params.order_by || "fullName";
  const mappedOrderBy = orderByMap[orderBy] || orderBy;

  const body = {
    page: page,
    page_size: pageSize,
    order_by: mappedOrderBy,
    order_direction: params.order_direction || "ASC",
  };

  // Add search if provided
  if (params.search) {
    if (typeof params.search === 'string' && params.search.trim() !== "") {
      body.search = { keyword: params.search.trim() };
    } else if (params.search?.keyword && params.search.keyword.trim() !== "") {
      body.search = { keyword: params.search.keyword.trim() };
    }
  }

  const response = await api.post("/v1/user/list", body);
  return response.data;
};

/**
 * Get user list with filters (uses /v1/tenant/user/list_filter endpoint)
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {Array<string>} params.order_by - Fields to order by (default: ["created_at", "full_name"])
 * @param {Array<string>} params.order_direction - Order direction (default: ["DESC", "ASC"])
 * @param {Object} params.search - Search object with keyword
 * @param {Object} params.filters - Filter object (status, roleUuids, etc.)
 * @param {string} params.cursor - Cursor for pagination
 * @returns {Promise<Object>} API response with body and meta
 * @throws {Error} If the API call fails
 */
/** /v1/workspace/user/list_filter only accepts these (snake_case) */
const WORKSPACE_USER_LIST_ORDER_BY_ALLOWED = new Set([
  "created_at",
  "updated_at",
  "full_name",
]);

const normalizeWorkspaceUserListOrderByField = (field) => {
  const aliasToApi = {
    createdAt: "created_at",
    fullName: "full_name",
    updatedAt: "updated_at",
    created_at: "created_at",
    full_name: "full_name",
    updated_at: "updated_at",
    name: "full_name",
    email: "full_name",
    status: "created_at",
  };
  const mapped = aliasToApi[field] ?? field;
  return WORKSPACE_USER_LIST_ORDER_BY_ALLOWED.has(mapped)
    ? mapped
    : "created_at";
};

export const getUserListFilters = async (params = {}) => {
  const rawOrderBy = params.order_by || ["created_at", "full_name"];
  const orderBy = Array.isArray(rawOrderBy)
    ? rawOrderBy.map((ob) => normalizeWorkspaceUserListOrderByField(ob))
    : [normalizeWorkspaceUserListOrderByField(rawOrderBy)];

  const body = {
    page: params.page || 1,
    page_size: params.page_size || 20,
    order_by: orderBy,
    order_direction: params.order_direction || ["DESC", "ASC"],
    filters: params.filters || null,
    cursor: params.cursor || null,
  };

  if (params.search) {
    if (typeof params.search === 'string' && params.search.trim() !== "") {
      body.search = { keyword: params.search.trim() };
    } else if (params.search?.keyword && params.search.keyword.trim() !== "") {
      body.search = { keyword: params.search.keyword.trim() };
    }
  }

  const response = await api.post("/v1/workspace/user/list_filter", body);
  const responseData = response.data;

  if (!responseData) {
    throw new Error('Invalid response from server');
  }

  const statusCode = responseData.status_code || responseData.statusCode || response?.status || 200;
  const msgCode = responseData.msg_code || responseData.msgCode || [];

  if (statusCode >= 200 && statusCode < 300) {
    return {
      status_code: statusCode,
      statusCode: statusCode,
      msg_code: msgCode,
      msgCode: msgCode,
      request_id: responseData.request_id || responseData.requestId || `req-${Date.now()}`,
      requestId: responseData.request_id || responseData.requestId || `req-${Date.now()}`,
      body: Array.isArray(responseData.body) ? responseData.body : [],
      meta: responseData.meta || null,
    };
  }

  const errorMessage = msgCode.length > 0 
    ? (msgCode[0].description || msgCode[0].code || 'An error occurred while fetching users.')
    : 'An error occurred while fetching users.';

  const error = new Error(errorMessage);
  error.response = {
    data: responseData,
    status: statusCode,
  };
  
  throw error;
};

/**
 * Deactivate one or multiple users
 * @param {string|string[]} userUuidOrUuids - Single user UUID or array of user UUIDs
 * @returns {Promise<Object|Object[]>} Response data
 */
export const deactivateUser = async (userUuidOrUuids) => {
  // Validate input before processing
  if (!userUuidOrUuids || (Array.isArray(userUuidOrUuids) && userUuidOrUuids.length === 0)) {
    throw new Error("At least one user UUID is required");
  }

  // Support both single userUuid and array of userUuids
  const userUuids = Array.isArray(userUuidOrUuids) ? userUuidOrUuids : [userUuidOrUuids];

  // If multiple users, use Promise.all to deactivate them in parallel
  if (userUuids.length > 1) {
    try {
      const promises = userUuids.map(userUuid => {
        const body = {
          user_uuid: userUuid,
          tenants_uuid: []
        };

        return api.post("/v1/user/deactivate", body);
      });

      const responses = await Promise.all(promises);
      const results = responses.map(response => {
        const responseData = response.data;

        // Check if the statusCode in the response body indicates an error
        if (responseData.statusCode && responseData.statusCode !== 200) {
          // Extract error message from msgCode
          let errorMessage = 'Failed to deactivate user';

          if (responseData.msgCode && Array.isArray(responseData.msgCode) && responseData.msgCode.length > 0) {
            errorMessage = responseData.msgCode[0].description || responseData.msgCode[0].code || errorMessage;
          } else if (responseData.message) {
            errorMessage = responseData.message;
          }

          // Create an error with the structure the component expects
          const error = new Error(errorMessage);
          error.response = {
            data: responseData,
            status: responseData.statusCode,
          };
          throw error;
        }

        return responseData;
      });

      // Return array of results for multiple users
      return results;
    } catch (error) {
      // If any request fails, throw the error
      throw error;
    }
  }

  // Single user deactivation (original behavior)
  const body = {
    user_uuid: userUuids[0],
    tenants_uuid: []
  };

  const response = await api.post("/v1/user/deactivate", body);
  const responseData = response.data;

  // Check if the statusCode in the response body indicates an error
  // Even if the HTTP response is 200, the body may contain statusCode 500, 400, etc.
  if (responseData.statusCode && responseData.statusCode !== 200) {
    // Extract error message from msgCode
    let errorMessage = 'Failed to deactivate user';

    if (responseData.msgCode && Array.isArray(responseData.msgCode) && responseData.msgCode.length > 0) {
      errorMessage = responseData.msgCode[0].description || responseData.msgCode[0].code || errorMessage;
    } else if (responseData.message) {
      errorMessage = responseData.message;
    }

    // Create an error with the structure the component expects
    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: responseData.statusCode,
    };
    throw error;
  }

  return responseData;
};

/**
 * Get user status list with filters (uses /v1/tenant/user/status/list_filter endpoint)
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {Array<string>} params.order_by - Fields to order by (default: ["status"])
 * @param {Array<string>} params.order_direction - Order direction (default: ["ASC"])
 * @param {Object} params.search - Search object with keyword
 * @returns {Promise<Object>} API response with body containing status list
 * @throws {Error} If the API call fails
 */
export const getUserStatusListFilter = async (params = {}) => {
  const body = {
    page: params.page || 1,
    page_size: params.page_size || 20,
    order_by: params.order_by || ["status"],
    order_direction: params.order_direction || ["ASC"],
  };

  const searchKeyword = typeof params.search === 'string' 
    ? params.search.trim() 
    : params.search?.keyword?.trim();
  
  if (searchKeyword) {
    body.search = { keyword: searchKeyword };
  }

  try {
    const response = await api.post("/v1/tenant/user/status/list_filter", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error('Invalid response from server');
    }

    const statusCode = responseData.status_code || responseData.statusCode || response?.status || 200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        statusCode: statusCode,
        msg_code: msgCode,
        msgCode: msgCode,
        request_id: responseData.request_id || responseData.requestId || `req-${Date.now()}`,
        requestId: responseData.request_id || responseData.requestId || `req-${Date.now()}`,
        body: Array.isArray(responseData.body) ? responseData.body : [],
        meta: responseData.meta || null,
      };
    }

    const errorMessage = msgCode.length > 0 
      ? (msgCode[0].description || msgCode[0].code || 'An error occurred while fetching user statuses.')
      : 'An error occurred while fetching user statuses.';

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };
    throw error;

  } catch (error) {
    if (error.response) {
      throw error;
    }

    if (error.request) {
      throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
    }

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      throw new Error('Request timeout. The server took too long to respond. Please try again.');
    }

    throw error;
  }
};

/**
 * Activate one or multiple users
 * @param {string|string[]} userUuidOrUuids - Single user UUID or array of user UUIDs
 * @returns {Promise<Object|Object[]>} Response data
 */
export const activateUser = async (userUuidOrUuids) => {
  // Validate input before processing
  if (!userUuidOrUuids || (Array.isArray(userUuidOrUuids) && userUuidOrUuids.length === 0)) {
    throw new Error("At least one user UUID is required");
  }

  // Support both single userUuid and array of userUuids
  const userUuids = Array.isArray(userUuidOrUuids) ? userUuidOrUuids : [userUuidOrUuids];

  // If multiple users, use Promise.all to activate them in parallel
  if (userUuids.length > 1) {
    try {
      const promises = userUuids.map(userUuid => {
        const body = {
          user_uuid: userUuid,
          tenants_uuid: []
        };

        return api.post("/v1/user/activate", body);
      });

      const responses = await Promise.all(promises);
      const results = responses.map(response => {
        const responseData = response.data;

        // Check if the statusCode in the response body indicates an error
        if (responseData.statusCode && responseData.statusCode !== 200) {
          // Extract error message from msgCode
          let errorMessage = 'Failed to activate user';

          if (responseData.msgCode && Array.isArray(responseData.msgCode) && responseData.msgCode.length > 0) {
            errorMessage = responseData.msgCode[0].description || responseData.msgCode[0].code || errorMessage;
          } else if (responseData.message) {
            errorMessage = responseData.message;
          }

          // Create an error with the structure the component expects
          const error = new Error(errorMessage);
          error.response = {
            data: responseData,
            status: responseData.statusCode,
          };
          throw error;
        }

        return responseData;
      });

      // Return array of results for multiple users
      return results;
    } catch (error) {
      // If any request fails, throw the error
      throw error;
    }
  }

  // Single user activation (original behavior)
  const body = {
    user_uuid: userUuids[0],
    tenants_uuid: []
  };

  const response = await api.post("/v1/user/activate", body);
  const responseData = response.data;

  // Check if the statusCode in the response body indicates an error
  // Even if the HTTP response is 200, the body may contain statusCode 500, 400, etc.
  if (responseData.statusCode && responseData.statusCode !== 200) {
    // Extract error message from msgCode
    let errorMessage = 'Failed to activate user';

    if (responseData.msgCode && Array.isArray(responseData.msgCode) && responseData.msgCode.length > 0) {
      errorMessage = responseData.msgCode[0].description || responseData.msgCode[0].code || errorMessage;
    } else if (responseData.message) {
      errorMessage = responseData.message;
    }

    // Create an error with the structure the component expects
    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: responseData.statusCode,
    };
    throw error;
  }

  return responseData;
};
